import { debounce } from "@evergis/sgis/es/utils/utils";
import { Input, PasswordToggle, useDialogShadow, useToggle } from "@evergis/ui";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { LayerInfo } from "../../../../../interfaces/resourceInfo";
import { formatDate } from "../../../../../utils/date";
import { isValidUrl } from "../../../../../utils/isValidUrl";
import {
  SUBDOMAINS_SEPARATOR,
  useAddProxyServiceContext,
} from "../../../AddProxyService/components/AddProxyServiceProvider";
import { ProxyServiceLayers } from "../../../AddProxyService/components/ProxyServiceLayers";

import { ProxyServiceMeta } from "../../../AddProxyService/components/ProxyServiceMeta";
import { ProxyServiceURL } from "../../../AddProxyService/components/ProxyServiceURL";
import { ProxyServiceType } from "../../../AddProxyService/components/types";
import { DialogActions } from "../../../DialogComponents/DialogActions";
import { FieldsRow } from "../../../DialogComponents/ResourceMeta/styled";
import { DialogContent, RestrictedWidthContainer } from "../../../DialogComponents/styled";
import { useUpdateProxyService } from "../../hooks/useUpdateProxyService";
import { PropertiesLabel, ResourceFieldValue } from "./styled";

interface ProxyLayerSettingsProps {
  dialogShadow: ReturnType<typeof useDialogShadow>;
  layer?: LayerInfo;
}

const DEBOUNCE_DELAY = 500;

export const ProxyLayerSettings: FC<ProxyLayerSettingsProps> = ({ dialogShadow, layer }) => {
  const { t } = useTranslation("dialogs");
  const { serviceType, sourceUrl, setSourceUrl, importLayers, layers, meta, subDomainsValue, setSubDomainsValue } =
    useAddProxyServiceContext();
  const [expanded, setExpanded] = useState(false);
  const [showPassword, toggleShowPassword] = useToggle(false);
  const { shadows, setScrollContainer } = dialogShadow;

  const { updateProxyLayer, proxyLoading } = useUpdateProxyService({
    name: meta.name,
    meta,
    serviceType,
    sourceUrl,
    layers,
    subDomains: subDomainsValue.replaceAll(" ", "").split(SUBDOMAINS_SEPARATOR),
  });

  const onExpand = useCallback(
    (expand: boolean) => {
      setExpanded(expand);
    },
    [setExpanded],
  );
  const debouncedLayersUpdate = useMemo(() => debounce(importLayers, DEBOUNCE_DELAY), [importLayers]);

  useEffect(() => {
    debouncedLayersUpdate();
  }, [serviceType, sourceUrl]);

  return (
    <>
      <DialogContent ref={setScrollContainer}>
        <RestrictedWidthContainer>
          <ProxyServiceMeta
            onExpand={onExpand}
            extra={<ResourceFieldValue field={t("editResource.updated")} value={formatDate(layer.changedDate)} />}
          />
        </RestrictedWidthContainer>
        {expanded && (
          <>
            <RestrictedWidthContainer>
              <PropertiesLabel>{t("editResource.connectionSettings")}</PropertiesLabel>
              <ProxyServiceURL
                sourceUrl={sourceUrl}
                setSourceUrl={setSourceUrl}
                serviceType={serviceType}
                subDomainsValue={subDomainsValue}
                setSubDomainsValue={setSubDomainsValue}
              />
              <FieldsRow style={{ marginTop: "1rem" }}>
                <Input type="text" label={t("addProxyService.username")} width="100%" />
                <Input type={showPassword ? "text" : "password"} label={t("addProxyService.password")} width="100%">
                  <PasswordToggle onClick={toggleShowPassword} isSelected={showPassword} />
                </Input>
              </FieldsRow>
            </RestrictedWidthContainer>
            {sourceUrl &&
              isValidUrl(sourceUrl) &&
              [ProxyServiceType.WMS, ProxyServiceType.ArcGIS].includes(serviceType) && <ProxyServiceLayers />}
          </>
        )}
      </DialogContent>
      <DialogActions
        applyDisabled={
          !sourceUrl ||
          !isValidUrl(sourceUrl) ||
          ([ProxyServiceType.WMS, ProxyServiceType.ArcGIS].includes(serviceType) && layers.length === 0)
        }
        onApply={updateProxyLayer}
        displayShadow={shadows.bottom}
        isWaiting={proxyLoading}
      />
    </>
  );
};
